import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FlaskConical, ArrowLeft, RotateCcw, Send, Play, Plus, Trash2, Edit3,
  CheckCircle2, XCircle, AlertCircle, Clock, BarChart3, ListChecks,
  ChevronDown, ChevronRight, Loader2, Search, FileText, Sparkles,
  Shield, ShieldAlert, Zap,
} from 'lucide-react';
import {
  listTestCases,
  createTestCase,
  updateTestCase,
  deleteTestCase,
  listTestRuns,
  runTestCases,
  getTestRun,
  createTestingSession,
  streamChatReply,
  sendSteerMessage,
  listPendingApprovals,
  streamSessionEvents,
  setSessionApprovalMode,
  decideApproval as decideApprovalRequest,
  listApprovalPreferences,
  revokeApprovalPreference,
  listQuickActionSuggestions,
  analyzeSessionQuickActions,
  acceptQuickActionSuggestion,
  dismissQuickActionSuggestion,
  type TestCase,
  type TestRun,
  type TestRunReport,
  type QuickActionSuggestion,
  type ApprovalMode,
  type PendingApproval,
  type ApprovalPreference,
} from '../api/testing';
import { getAgent } from '../api/agents';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import HitlDiffPreview, { type HitlDiffPayload } from '../components/HitlDiffPreview';

/** P10 审批四档 */
const APPROVAL_MODES: { value: ApprovalMode; label: string; hint: string; danger?: boolean }[] = [
  { value: 'always-ask', label: '每次询问', hint: '所有操作都需人工确认（默认）' },
  { value: 'read-only', label: '只读放行', hint: '只读类操作自动放行，写操作仍需确认' },
  { value: 'allow-all', label: '全部放行', hint: '所有操作自动放行，不再询问' },
  { value: 'deny-all', label: '全部拒绝', hint: '危险：所有操作一律拒绝执行', danger: true },
];

interface Message {
  id: string;
  role: 'user' | 'agent';
  content: string;
  created_at: string;
  /** P10：气泡角标（如「插话·已送达」「排队·已自动发出」） */
  badge?: string;
}

type TabKey = 'cases' | 'chat' | 'reports';

/** 提取后端错误文案（detail 优先，其次 Error.message） */
const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

export default function TestingChat() {
  const params = useParams();
  // 路由参数按存在处理：缺省时保持与原模板字符串一致的 `/agents/undefined/...` 请求行为
  const agentId = params.agentId as string;
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [activeTab, setActiveTab] = useState<TabKey>('cases');

  // ── Test Cases ──
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(new Set());
  const [caseSearch, setCaseSearch] = useState('');
  const [showCaseForm, setShowCaseForm] = useState(false);
  const [editingCase, setEditingCase] = useState<TestCase | null>(null);
  const [caseForm, setCaseForm] = useState({
    name: '',
    description: '',
    input_text: '',
    expected_output: '',
    expected_contains: '',
    match_type: 'contains',
    tags: '',
    is_enabled: true,
  });

  // ── Chat ──
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const chatEnd = useRef<HTMLDivElement>(null);

  // ── Quick Action 建议（P1） ──
  const [qaSuggestions, setQaSuggestions] = useState<QuickActionSuggestion[]>([]);
  const [qaAnalyzing, setQaAnalyzing] = useState(false);
  const [qaActioning, setQaActioning] = useState<string | null>(null);

  // ── P10 审批四档 + 运行中插话 ──
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>('always-ask');
  const [modeUpdating, setModeUpdating] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [approvalActioning, setApprovalActioning] = useState<string | null>(null);

  // ── B3 审批偏好记忆 ──
  /** 按类别索引的偏好（已记住决策 + 命中进度），审批卡片展示可撤回入口 */
  const [approvalPrefs, setApprovalPrefs] = useState<Record<string, ApprovalPreference>>({});
  /** 每张审批卡片「记住我的选择」勾选状态（key = 审批消息 id） */
  const [rememberById, setRememberById] = useState<Record<string, boolean>>({});
  const [prefActioning, setPrefActioning] = useState<string | null>(null);

  // ── Reports ──
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runReport, setRunReport] = useState<TestRunReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [runningTests, setRunningTests] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const [agentLlmConfigured, setAgentLlmConfigured] = useState<boolean | null>(null);

  // ── Fetchers ──
  const fetchTestCases = useCallback(async () => {
    setLoadingCases(true);
    try {
      const data = await listTestCases(agentId);
      setTestCases(data);
    } catch (e) {
      toast.error('加载测试用例失败: ' + errorMessage(e));
    } finally {
      setLoadingCases(false);
    }
  }, [agentId, toast]);

  const fetchTestRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const data = await listTestRuns(agentId);
      setTestRuns(data);
    } catch (e) {
      toast.error('加载测试报告失败: ' + errorMessage(e));
    } finally {
      setLoadingRuns(false);
    }
  }, [agentId, toast]);

  // ── Quick Action 建议（P1）──
  // 拉取该 Agent 的 pending 建议；静默失败（无建议/接口异常均不打扰 UI）
  const fetchQaSuggestions = useCallback(async () => {
    try {
      const data = await listQuickActionSuggestions(agentId);
      setQaSuggestions(data);
    } catch {
      /* 保持安静 */
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      if (activeTab === 'cases') void fetchTestCases();
      if (activeTab === 'reports') void fetchTestRuns();
      if (activeTab === 'chat') void fetchQaSuggestions();
    };
    tick();
  }, [activeTab, fetchTestCases, fetchTestRuns, fetchQaSuggestions]);

  // ── Quick Action 建议（P1）──
  // 会话结束后触发后台分析；分类为 none 或 LLM 不可用时无建议，保持安静
  const analyzeSession = async () => {
    if (!sessionId || messages.length === 0) {
      toast.info('请先进行几轮对话再分析');
      return;
    }
    setQaAnalyzing(true);
    try {
      const suggestion = await analyzeSessionQuickActions(agentId, sessionId);
      if (suggestion) {
        setQaSuggestions(prev => [suggestion, ...prev.filter(s => s.id !== suggestion.id)]);
      } else {
        toast.info('本次会话属于一次性工作，无需沉淀技能');
      }
    } catch (e) {
      toast.error('分析失败: ' + errorMessage(e));
    } finally {
      setQaAnalyzing(false);
    }
  };

  const acceptQaSuggestion = async (id: string) => {
    setQaActioning(id);
    try {
      await acceptQuickActionSuggestion(id);
      setQaSuggestions(prev => prev.filter(s => s.id !== id));
      toast.success('已保存为技能，可在「配置中心 → 技能」查看');
    } catch (e) {
      toast.error('保存失败: ' + errorMessage(e));
    } finally {
      setQaActioning(null);
    }
  };

  const dismissQaSuggestion = async (id: string) => {
    setQaActioning(id);
    try {
      await dismissQuickActionSuggestion(id);
      setQaSuggestions(prev => prev.filter(s => s.id !== id));
      toast.info('已忽略该建议');
    } catch (e) {
      toast.error('操作失败: ' + errorMessage(e));
    } finally {
      setQaActioning(null);
    }
  };

  useEffect(() => {
    const checkAgent = async () => {
      try {
        const me = await getAgent(agentId);
        setAgentLlmConfigured(Boolean(me.llm_config_id));
      } catch {
        setAgentLlmConfigured(null);
      }
    };
    checkAgent();
  }, [agentId]);

  // ── B3：加载当前 Agent 的审批偏好（按类别索引，审批卡片展示可撤回入口） ──
  const loadPreferences = useCallback(async () => {
    if (!agentId) return;
    try {
      const preferences = await listApprovalPreferences(agentId);
      const map: Record<string, ApprovalPreference> = {};
      for (const p of preferences) map[p.category] = p;
      setApprovalPrefs(map);
    } catch {
      /* 偏好拉取失败不打扰审批 */
    }
  }, [agentId]);

  // ── P3-④ 审批进流：先全量拉一次 pending（补漏），再挂 SSE 增量推送；
  // SSE 不可用（端点 404 / 连接失败 / 流中断）时回退 5s 轮询兜底。 ──
  useEffect(() => {
    if (activeTab !== 'chat' || !sessionId) return;
    let cancelled = false;
    const controller = new AbortController();
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const fetchPending = async () => {
      try {
        const list = await listPendingApprovals(sessionId);
        if (!cancelled) setPendingApprovals(list);
      } catch {
        /* 审批请求拉取失败不打断对话 */
      }
    };

    // SSE 不可用时的兜底轮询（幂等启动）
    const startPollingFallback = () => {
      if (cancelled || pollTimer) return;
      pollTimer = setInterval(fetchPending, 5000);
    };

    // 1. 全量拉一次（补 SSE 断连期间的历史；审批请求本身是持久化消息）
    fetchPending();
    // B3：同时刷新审批偏好（审批卡片展示「记住选择/可撤回」提示）
    void loadPreferences();

    // 2. 挂 SSE 实时增量：REQUEST 追加（按 approval_id 去重）、RESOLVED 移除
    (async () => {
      try {
        await streamSessionEvents(sessionId, {
          onApprovalRequest: (frame) => {
            if (cancelled) return;
            setPendingApprovals(prev => {
              if (prev.some(a => a.id === frame.approval_id)) return prev;
              return [...prev, {
                id: frame.approval_id,
                content: frame.content ?? '',
                feedback: {
                  status: 'pending',
                  operation: frame.operation ?? undefined,
                  operation_kind: frame.operation_kind ?? undefined,
                  read_only: frame.read_only ?? undefined,
                  diff: frame.diff ?? undefined,
                  category: frame.category ?? undefined,
                },
              }];
            });
          },
          onApprovalResolved: (frame) => {
            if (cancelled) return;
            setPendingApprovals(prev => prev.filter(a => a.id !== frame.approval_id));
          },
        }, controller.signal);
        // 流正常结束（服务端关闭/断开）：回退轮询
        startPollingFallback();
      } catch {
        // SSE 连接失败 / 端点 404（SESSION_EVENTS_ENABLED=false）：回退轮询
        startPollingFallback();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [activeTab, sessionId, loadPreferences]);

  // ── P10：切换审批模式（中途可改、即时生效；失败回滚并提示） ──
  const changeApprovalMode = async (mode: ApprovalMode) => {
    if (!sessionId || mode === approvalMode || modeUpdating) return;
    const previous = approvalMode;
    setModeUpdating(true);
    try {
      await setSessionApprovalMode(sessionId, mode);
      setApprovalMode(mode);
      const label = APPROVAL_MODES.find(m => m.value === mode)?.label || mode;
      toast.success(`审批模式已切换为「${label}」，立即生效`);
    } catch (e) {
      setApprovalMode(previous);
      toast.error('切换审批模式失败: ' + errorMessage(e));
    } finally {
      setModeUpdating(false);
    }
  };

  // ── P10：人工审批决策（允许 / 拒绝） ──
  const handleDecideApproval = async (id: string, decision: 'allow' | 'deny') => {
    if (!sessionId) return;
    setApprovalActioning(id);
    // B3：勾选「记住我的选择」且该请求带 category 时，随决策提交偏好
    const card = pendingApprovals.find(a => a.id === id);
    const remember = rememberById[id] ?? false;
    const category = card?.feedback?.category;
    try {
      await decideApprovalRequest(sessionId, id, {
        decision,
        ...(remember && category ? { remember: true, category } : {}),
      });
      setPendingApprovals(prev => prev.filter(a => a.id !== id));
      if (remember && category) {
        toast.success(
          decision === 'allow'
            ? `已允许并记住「${category}」选择：同类问题累计达到阈值后将自动放行（可撤回）`
            : `已拒绝并记住「${category}」选择：同类问题累计达到阈值后将自动拒绝（可撤回）`
        );
        loadPreferences();
      } else {
        toast.success(decision === 'allow' ? '已允许，操作将继续执行' : '已拒绝该操作');
      }
    } catch (e) {
      toast.error('审批决策失败: ' + errorMessage(e));
    } finally {
      setApprovalActioning(null);
    }
  };

  // ── B3：撤回单条偏好（同类问题恢复人工确认） ──
  const revokePreference = async (category: string) => {
    if (!agentId) return;
    setPrefActioning(category);
    try {
      await revokeApprovalPreference(agentId, category);
      toast.success(`已撤回「${category}」偏好，同类问题将恢复人工确认`);
      setApprovalPrefs(prev => {
        const next = { ...prev };
        delete next[category];
        return next;
      });
    } catch (e) {
      toast.error('撤回偏好失败: ' + errorMessage(e));
    } finally {
      setPrefActioning(null);
    }
  };

  // ── P10：运行中插话 / 排队 ──
  const sendSteer = async (mode: 'steering' | 'queued') => {
    if (!sessionId || !chatInput.trim()) return;
    const text = chatInput.trim();
    setChatInput('');
    try {
      await sendSteerMessage(sessionId, text, mode);
      if (mode === 'steering') {
        toast.success('插话已记录，将随下一轮送达 Agent');
      } else {
        toast.success('已加入排队，本轮结束后自动发出');
      }
    } catch (e) {
      toast.error((mode === 'steering' ? '插话失败: ' : '排队失败: ') + errorMessage(e));
    }
  };

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Case CRUD ──
  const resetCaseForm = () => {
    setCaseForm({
      name: '', description: '', input_text: '', expected_output: '',
      expected_contains: '', match_type: 'contains', tags: '', is_enabled: true,
    });
    setEditingCase(null);
  };

  const openCreateCase = () => {
    resetCaseForm();
    setShowCaseForm(true);
  };

  const openEditCase = (tc: TestCase) => {
    setEditingCase(tc);
    setCaseForm({
      name: tc.name,
      description: tc.description || '',
      input_text: tc.input_text,
      expected_output: tc.expected_output || '',
      expected_contains: tc.expected_contains?.join('\n') || '',
      match_type: tc.match_type,
      tags: tc.tags?.join(', ') || '',
      is_enabled: tc.is_enabled,
    });
    setShowCaseForm(true);
  };

  const submitCaseForm = async () => {
    if (!caseForm.name.trim() || !caseForm.input_text.trim()) {
      toast.error('名称和输入文本为必填项');
      return;
    }
    const payload = {
      name: caseForm.name.trim(),
      description: caseForm.description.trim() || undefined,
      input_text: caseForm.input_text.trim(),
      expected_output: caseForm.expected_output.trim() || undefined,
      expected_contains: caseForm.expected_contains
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean) || undefined,
      match_type: caseForm.match_type,
      tags: caseForm.tags
        .split(',')
        .map(s => s.trim())
        .filter(Boolean) || undefined,
      is_enabled: caseForm.is_enabled,
    };
    try {
      if (editingCase) {
        await updateTestCase(agentId, editingCase.id, payload);
        toast.success('测试用例已更新');
      } else {
        await createTestCase(agentId, payload);
        toast.success('测试用例已创建');
      }
      setShowCaseForm(false);
      resetCaseForm();
      fetchTestCases();
    } catch (e) {
      toast.error('保存失败: ' + errorMessage(e));
    }
  };

  const deleteCase = async (id: string) => {
    if (!(await confirm({ title: '确定删除此测试用例？', danger: true }))) return;
    try {
      await deleteTestCase(agentId, id);
      toast.success('已删除');
      fetchTestCases();
      setSelectedCaseIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      toast.error('删除失败: ' + errorMessage(e));
    }
  };

  const toggleSelectCase = (id: string) => {
    setSelectedCaseIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    const visible = filteredCases;
    if (selectedCaseIds.size === visible.length && visible.length > 0) {
      setSelectedCaseIds(new Set());
    } else {
      setSelectedCaseIds(new Set(visible.map(c => c.id)));
    }
  };

  const filteredCases = testCases.filter(c =>
    c.name.toLowerCase().includes(caseSearch.toLowerCase()) ||
    c.input_text.toLowerCase().includes(caseSearch.toLowerCase())
  );

  // ── Chat ──
  const startChat = async () => {
    try {
      const session = await createTestingSession(agentId);
      setSessionId(session.id);
      setApprovalMode((session.approval_mode as ApprovalMode) || 'always-ask');
      setPendingApprovals([]);
    } catch (e) {
      toast.error('开始测试失败: ' + errorMessage(e));
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !sessionId) return;
    const text = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    const streamId = `stream-${Date.now()}`;
    const now = new Date().toISOString();
    setMessages(prev => [
      ...prev,
      { id: Date.now().toString(), role: 'user', content: text, created_at: now },
      { id: streamId, role: 'agent', content: '', created_at: now },
    ]);
    let streamError: string | null = null;
    try {
      await streamChatReply(sessionId, { message: text, agent_id: agentId }, {
        // TOKEN 帧逐字追加到当前气泡
        onToken: (chunk) => {
          setMessages(prev => prev.map(m => (m.id === streamId ? { ...m, content: m.content + chunk } : m)));
        },
        // DONE 帧收尾：插话/排队回执以角标气泡插在回复气泡前
        onDone: (done) => {
          const injected: Message[] = (done.steering_injected || []).map(m => ({
            id: `steer-${m.id}`,
            role: 'user' as const,
            content: m.content,
            created_at: now,
            badge: '插话·已随本轮送达',
          }));
          const queued: Message[] = (done.queued_delivered || []).map(m => ({
            id: `queued-${m.id}`,
            role: 'user' as const,
            content: m.content,
            created_at: now,
            badge: '排队·本轮结束自动发出',
          }));
          setMessages(prev => {
            const idx = prev.findIndex(m => m.id === streamId);
            if (idx === -1) return prev;
            const finalAgent: Message = { ...prev[idx], id: done.message_id || prev[idx].id };
            return [...prev.slice(0, idx), ...injected, ...queued, finalAgent];
          });
        },
        // ERROR 帧（中道失败）：流已结束，降级为错误文案气泡
        onError: (content) => {
          streamError = content;
        },
      });
      if (streamError) {
        const detail = streamError;
        setMessages(prev => prev.map(m => (m.id === streamId ? { ...m, content: `⚠️ ${detail}` } : m)));
        toast.error('发送失败: ' + detail);
      }
    } catch (e) {
      const detail = errorMessage(e);
      toast.error('发送失败: ' + detail);
      if (typeof detail === 'string' && detail.toLowerCase().includes('llm')) {
        setMessages(prev => prev.map(m => (m.id === streamId ? {
          ...m,
          content: '当前 Agent 缺少 LLM 配置，请前往「配置中心 → LLM 配置」添加可用模型后再测试。',
        } : m)));
      } else {
        setMessages(prev => prev.map(m => (m.id === streamId ? { ...m, content: '对话失败，请检查 LLM 配置' } : m)));
      }
    } finally {
      setChatLoading(false);
    }
  };

  // ── Run Tests ──
  const runSelectedTests = async () => {
    const ids = Array.from(selectedCaseIds);
    if (ids.length === 0) {
      toast.error('请至少选择一个测试用例');
      return;
    }
    setRunningTests(true);
    try {
      const report = await runTestCases(agentId, {
        testcase_ids: ids,
        name: `批量测试 (${ids.length} 条)`,
      });
      toast.success('测试执行完成');
      setActiveTab('reports');
      setSelectedRunId(report.id);
      setRunReport(report);
      fetchTestRuns();
    } catch (e) {
      const detail = errorMessage(e);
      toast.error('执行失败: ' + detail);
      if (typeof detail === 'string' && detail.toLowerCase().includes('llm')) {
        toast.info('提示：当前 Agent 缺少 LLM 配置，请前往配置中心设置');
      }
    } finally {
      setRunningTests(false);
    }
  };

  const loadReport = async (runId: string) => {
    setSelectedRunId(runId);
    setLoadingReport(true);
    try {
      const report = await getTestRun(runId);
      setRunReport(report);
    } catch (e) {
      toast.error('加载报告失败: ' + errorMessage(e));
    } finally {
      setLoadingReport(false);
    }
  };

  const toggleResultExpand = (id: string) => {
    setExpandedResults(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // ── Render helpers ──
  const statusBadge = (status: string) => {
    const map: Record<string, { cls: string; icon: React.ReactNode }> = {
      passed: { cls: 'badge-success', icon: <CheckCircle2 size={12} /> },
      failed: { cls: 'badge-danger', icon: <XCircle size={12} /> },
      error: { cls: 'badge-danger', icon: <AlertCircle size={12} /> },
      skipped: { cls: 'badge-warning', icon: <AlertCircle size={12} /> },
      running: { cls: 'badge-info', icon: <Loader2 size={12} className="spin" /> },
      completed: { cls: 'badge-success', icon: <CheckCircle2 size={12} /> },
      pending: { cls: 'badge-muted', icon: <Clock size={12} /> },
    };
    const s = map[status] || map.pending;
    return <span className={`badge ${s.cls}`}>{s.icon} {status}</span>;
  };

  const passRateColor = (rate: number | null) => {
    if (rate === null) return 'var(--muted)';
    if (rate >= 0.9) return 'var(--success)';
    if (rate >= 0.7) return 'var(--warning)';
    return 'var(--danger)';
  };

  return (
    <div className="container" style={{ paddingBottom: 24 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4" style={{ gap: 12, flexWrap: 'wrap' }}>
        <button className="secondary" onClick={() => navigate(`/agents/${agentId}`)}>
          <ArrowLeft size={16} /> 返回概览
        </button>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FlaskConical size={22} /> 测试验证中心
        </h2>
        <div className="flex" style={{ gap: 4 }}>
          {(['cases', 'chat', 'reports'] as TabKey[]).map(tab => (
            <button
              key={tab}
              className={activeTab === tab ? 'primary' : 'secondary'}
              onClick={() => setActiveTab(tab)}
              style={{ fontSize: 13 }}
            >
              {tab === 'cases' && <><ListChecks size={14} style={{ marginRight: 4 }} />用例管理</>}
              {tab === 'chat' && <><FileText size={14} style={{ marginRight: 4 }} />自由测试</>}
              {tab === 'reports' && <><BarChart3 size={14} style={{ marginRight: 4 }} />执行报告</>}
            </button>
          ))}
        </div>
      </div>

      {agentLlmConfigured === false && (
        <div
          className="card mb-4"
          style={{
            borderColor: 'var(--color-warning)',
            background: 'rgba(251, 191, 36, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div className="flex items-center gap-2" style={{ color: 'var(--color-warning)' }}>
            <AlertCircle size={18} />
            <span>当前 Agent 未配置 LLM，测试执行可能失败。</span>
          </div>
          <button className="primary" onClick={() => navigate(`/agents/${agentId}/config`)}>
            前往 LLM 配置
          </button>
        </div>
      )}

      {/* ═══════ TAB: Test Cases ═══════ */}
      {activeTab === 'cases' && (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-3" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div className="flex items-center" style={{ gap: 8, flex: 1, minWidth: 200 }}>
              <Search size={16} className="muted" />
              <input
                type="text"
                placeholder="搜索用例名称或输入..."
                value={caseSearch}
                onChange={e => setCaseSearch(e.target.value)}
                style={{ minWidth: 220 }}
              />
            </div>
            <div className="flex" style={{ gap: 8 }}>
              <button className="primary" onClick={openCreateCase}><Plus size={14} /> 新建用例</button>
              <button
                className="primary"
                onClick={runSelectedTests}
                disabled={runningTests || selectedCaseIds.size === 0}
              >
                {runningTests ? <Loader2 size={14} className="spin" /> : <Play size={14} />}
                执行选中 ({selectedCaseIds.size})
              </button>
            </div>
          </div>

          {/* Case Table */}
          <div className="card" style={{ overflow: 'auto' }}>
            <table className="data-table" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={filteredCases.length > 0 && selectedCaseIds.size === filteredCases.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>名称</th>
                  <th>输入</th>
                  <th>匹配方式</th>
                  <th>预期输出</th>
                  <th>关键词</th>
                  <th style={{ width: 80 }}>状态</th>
                  <th style={{ width: 100 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {loadingCases ? (
                  <tr><td colSpan={8} className="text-center muted py-4"><Loader2 size={18} className="spin" /> 加载中...</td></tr>
                ) : filteredCases.length === 0 ? (
                  <tr><td colSpan={8} className="text-center muted py-4">暂无测试用例</td></tr>
                ) : filteredCases.map(tc => (
                  <tr key={tc.id} className={!tc.is_enabled ? 'opacity-60' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCaseIds.has(tc.id)}
                        onChange={() => toggleSelectCase(tc.id)}
                      />
                    </td>
                    <td><strong>{tc.name}</strong></td>
                    <td><div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tc.input_text}</div></td>
                    <td><span className="badge badge-muted">{tc.match_type}</span></td>
                    <td><div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tc.expected_output || '-'}</div></td>
                    <td>
                      {tc.expected_contains?.map((k, i) => (
                        <span key={i} className="badge badge-info" style={{ marginRight: 4, marginBottom: 2 }}>{k}</span>
                      )) || '-'}
                    </td>
                    <td>{tc.is_enabled ? <span className="badge badge-success">启用</span> : <span className="badge badge-warning">停用</span>}</td>
                    <td>
                      <div className="flex" style={{ gap: 4 }}>
                        <button className="icon-btn" title="编辑" onClick={() => openEditCase(tc)}><Edit3 size={14} /></button>
                        <button className="icon-btn danger" title="删除" onClick={() => deleteCase(tc.id)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Case Form Modal */}
          {showCaseForm && (
            <div className="modal-overlay" onClick={() => setShowCaseForm(false)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
                <h3>{editingCase ? '编辑测试用例' : '新建测试用例'}</h3>
                <div className="form-group">
                  <label>名称 *</label>
                  <input value={caseForm.name} onChange={e => setCaseForm(f => ({ ...f, name: e.target.value }))} placeholder="例如: 问候语测试" />
                </div>
                <div className="form-group">
                  <label>描述</label>
                  <input value={caseForm.description} onChange={e => setCaseForm(f => ({ ...f, description: e.target.value }))} placeholder="简要描述测试目的" />
                </div>
                <div className="form-group">
                  <label>输入文本 *</label>
                  <textarea
                    rows={3}
                    value={caseForm.input_text}
                    onChange={e => setCaseForm(f => ({ ...f, input_text: e.target.value }))}
                    placeholder="发送给 Agent 的消息"
                  />
                </div>
                <div className="form-group">
                  <label>匹配方式</label>
                  <select value={caseForm.match_type} onChange={e => setCaseForm(f => ({ ...f, match_type: e.target.value }))}>
                    <option value="contains">包含匹配 (contains)</option>
                    <option value="exact">精确匹配 (exact)</option>
                    <option value="any">任意关键词 (any)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>预期输出</label>
                  <textarea
                    rows={2}
                    value={caseForm.expected_output}
                    onChange={e => setCaseForm(f => ({ ...f, expected_output: e.target.value }))}
                    placeholder="期望 Agent 回复中包含的完整文本"
                  />
                </div>
                <div className="form-group">
                  <label>预期关键词（每行一个）</label>
                  <textarea
                    rows={3}
                    value={caseForm.expected_contains}
                    onChange={e => setCaseForm(f => ({ ...f, expected_contains: e.target.value }))}
                    placeholder={`你好\n欢迎\n谢谢`}
                  />
                </div>
                <div className="form-group">
                  <label>标签（逗号分隔）</label>
                  <input value={caseForm.tags} onChange={e => setCaseForm(f => ({ ...f, tags: e.target.value }))} placeholder="基础对话, 知识问答" />
                </div>
                <div className="form-group flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={caseForm.is_enabled}
                    onChange={e => setCaseForm(f => ({ ...f, is_enabled: e.target.checked }))}
                    id="tc-enabled"
                  />
                  <label htmlFor="tc-enabled" style={{ margin: 0 }}>启用此用例</label>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button className="secondary" onClick={() => setShowCaseForm(false)}>取消</button>
                  <button className="primary" onClick={submitCaseForm}>保存</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB: Free Chat ═══════ */}
      {activeTab === 'chat' && (
        <>
          {!sessionId ? (
            <div className="card text-center" style={{ maxWidth: 500, margin: '60px auto' }}>
              <h3>自由对话测试</h3>
              <p className="muted">与 Agent 进行任意对话，验证其回复质量。</p>
              <button className="primary" onClick={startChat} style={{ padding: '14px 28px', fontSize: '16px' }}>
                <FlaskConical size={16} style={{ marginRight: 6 }} /> 开始测试
              </button>
            </div>
          ) : (
            <div className="card" style={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
              <div className="flex items-center justify-between mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className="muted">会话: {sessionId.slice(0, 8)}...</span>
                <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {/* P10 审批四档：会话中途可改、即时生效 */}
                  <div className="flex items-center" style={{ gap: 4 }} title="审批模式：控制 Agent 操作是否需要人工确认">
                    <Shield size={14} className="muted" />
                    {APPROVAL_MODES.map(m => (
                      <button
                        key={m.value}
                        className={approvalMode === m.value ? 'primary' : 'secondary'}
                        onClick={() => changeApprovalMode(m.value)}
                        disabled={modeUpdating}
                        title={m.hint}
                        style={{
                          fontSize: 12,
                          padding: '4px 8px',
                          ...(m.danger && approvalMode === m.value
                            ? { background: 'var(--danger)', borderColor: 'var(--danger)' }
                            : m.danger
                              ? { color: 'var(--danger)' }
                              : {}),
                        }}
                      >
                        {m.danger && <ShieldAlert size={12} style={{ marginRight: 2 }} />}
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <button className="secondary" onClick={analyzeSession} disabled={qaAnalyzing}>
                    {qaAnalyzing ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} 沉淀技能
                  </button>
                  <button className="secondary" onClick={() => { setMessages([]); setSessionId(null); setPendingApprovals([]); }}>
                    <RotateCcw size={14} /> 重新开始
                  </button>
                </div>
              </div>
              {/* P10 待审批：内联审批卡片（含 P4 diff 预览），允许/拒绝即时落审计
                  B3：卡片可勾选「记住我的选择」，已有偏好时展示命中进度与撤回入口 */}
              {pendingApprovals.map(a => (
                <div
                  key={a.id}
                  className="card mb-3"
                  style={{ borderColor: 'var(--color-warning)', background: 'rgba(251, 191, 36, 0.08)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert size={16} style={{ color: 'var(--color-warning)' }} />
                    <strong>待审批</strong>
                    <span className={`badge ${a.feedback?.read_only ? 'badge-info' : 'badge-warning'}`}>
                      {a.feedback?.read_only ? '只读操作' : '写操作'}
                    </span>
                    {a.feedback?.operation && <code className="muted" style={{ fontSize: 12 }}>{a.feedback.operation}</code>}
                    {a.feedback?.category && <span className="badge badge-info">{a.feedback.category}</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>{a.content}</div>
                  {a.feedback?.diff && (
                    <div style={{ marginBottom: 8 }}>
                      <HitlDiffPreview diff={a.feedback.diff as unknown as HitlDiffPayload} />
                    </div>
                  )}
                  {/* B3：已有偏好时展示命中进度 / 自动处理提示（含可撤回入口） */}
                  {a.feedback?.category && approvalPrefs[a.feedback.category] && (() => {
                    const pref = approvalPrefs[a.feedback.category];
                    const actionLabel = pref.decision === 'allow' ? '放行' : '拒绝';
                    return (
                      <div
                        className="flex items-center justify-between mb-2"
                        style={{
                          fontSize: 12,
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid var(--border)',
                          background: pref.auto_enabled ? 'rgba(34, 197, 94, 0.12)' : 'var(--surface)',
                          color: pref.auto_enabled ? 'var(--success)' : 'var(--muted)',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {pref.auto_enabled ? (
                            <>
                              <Zap size={13} />
                              该类别已按你的偏好自动{actionLabel}同类问题（可撤回）
                            </>
                          ) : (
                            <>
                              <CheckCircle2 size={13} />
                              已记住偏好（{pref.decision === 'allow' ? '放行' : '拒绝'}），
                              同类问题累计 {pref.hit_count}/{pref.threshold} 次后自动处理
                            </>
                          )}
                        </span>
                        <button
                          className="secondary"
                          style={{ fontSize: 11, padding: '2px 6px' }}
                          onClick={() => revokePreference(pref.category)}
                          disabled={prefActioning === pref.category}
                        >
                          {prefActioning === pref.category ? <Loader2 size={11} className="spin" /> : <XCircle size={11} />} 撤回偏好
                        </button>
                      </div>
                    );
                  })()}
                  <label className="flex items-center mb-2" style={{ gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={rememberById[a.id] ?? false}
                      onChange={e => setRememberById(prev => ({ ...prev, [a.id]: e.target.checked }))}
                    />
                    记住我的选择
                    <span className="muted" style={{ fontSize: 12 }}>
                      {a.feedback?.category
                        ? '（同类问题累计达到阈值后自动按此处理，可撤回）'
                        : '（需审批请求带问题类别，当前不可用）'}
                    </span>
                  </label>
                  <div className="flex justify-end" style={{ gap: 8 }}>
                    <button
                      className="secondary"
                      onClick={() => handleDecideApproval(a.id, 'deny')}
                      disabled={approvalActioning === a.id}
                      style={{ color: 'var(--danger)' }}
                    >
                      <XCircle size={14} /> 拒绝
                    </button>
                    <button className="primary" onClick={() => handleDecideApproval(a.id, 'allow')} disabled={approvalActioning === a.id}>
                      {approvalActioning === a.id ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} 允许执行
                    </button>
                  </div>
                </div>
              ))}
              {/* Quick Action 建议卡片：分析出可复用操作时展示，接受后落入配置中心技能 */}
              {qaSuggestions.map(s => (
                <div
                  key={s.id}
                  className="card mb-3"
                  style={{ borderColor: 'var(--primary)', background: 'var(--primary-light)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} style={{ color: 'var(--primary)' }} />
                    <strong>{s.payload.label}</strong>
                    <span className="badge badge-info">{s.kind === 'command' ? '确定性命令' : '智能技能'}</span>
                  </div>
                  {s.payload.explanation && (
                    <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>{s.payload.explanation}</div>
                  )}
                  <div
                    style={{
                      fontSize: 13,
                      padding: 8,
                      background: 'var(--surface)',
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      whiteSpace: 'pre-wrap',
                      maxHeight: 120,
                      overflow: 'auto',
                      marginBottom: 8,
                    }}
                  >
                    {s.kind === 'command' ? s.payload.command : s.payload.instruction}
                  </div>
                  <div className="flex justify-end" style={{ gap: 8 }}>
                    <button className="secondary" onClick={() => dismissQaSuggestion(s.id)} disabled={qaActioning === s.id}>
                      忽略
                    </button>
                    <button className="primary" onClick={() => acceptQaSuggestion(s.id)} disabled={qaActioning === s.id}>
                      {qaActioning === s.id ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} 存为技能
                    </button>
                  </div>
                </div>
              ))}
              <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                {messages.length === 0 && (
                  <div className="empty text-center muted" style={{ padding: 40 }}>
                    <p>输入问题开始测试</p>
                  </div>
                )}
                {messages.map(msg => (
                  <div key={msg.id} style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: 10,
                  }}>
                    <div style={{ maxWidth: '70%' }}>
                      {msg.badge && (
                        <div className="muted" style={{ fontSize: 11, marginBottom: 2, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                          {msg.badge}
                        </div>
                      )}
                      <div style={{
                        padding: '10px 14px',
                        borderRadius: 12,
                        background: msg.role === 'user' ? 'var(--primary)' : 'var(--surface)',
                        color: msg.role === 'user' ? '#fff' : 'var(--text)',
                        border: msg.role === 'user' ? 'none' : '1px solid var(--border)',
                        fontSize: 14,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                    <div style={{ padding: '10px 14px', borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-2">
                        <div className="spinner" style={{ width: 14, height: 14 }} />
                        <span className="muted">思考中...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEnd} />
              </div>
              {/* P10：运行中（等待回复）时输入框保持可用，Enter 即插话；也可选择排队 */}
              {chatLoading && (
                <div className="flex items-center gap-2 muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  <Zap size={12} />
                  Agent 回复中——可直接输入并回车「插话」（随下一轮送达），或点击「排队」在本轮结束后自动发出。
                </div>
              )}
              <div className="flex" style={{ gap: 8 }}>
                <input
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  placeholder={chatLoading ? '输入插话内容，回车随下一轮送达...' : '输入测试问题...'}
                  onKeyDown={e => {
                    if (e.key !== 'Enter') return;
                    if (chatLoading) void sendSteer('steering');
                    else void sendChatMessage();
                  }}
                  style={{ flex: 1 }}
                />
                {chatLoading ? (
                  <>
                    <button className="secondary" onClick={() => sendSteer('steering')} disabled={!chatInput.trim()} title="随下一轮送达运行中的 Agent">
                      <Zap size={14} /> 插话
                    </button>
                    <button className="secondary" onClick={() => sendSteer('queued')} disabled={!chatInput.trim()} title="本轮结束后自动发出">
                      <Clock size={14} /> 排队
                    </button>
                  </>
                ) : (
                  <button className="primary" onClick={sendChatMessage} disabled={!chatInput.trim()}>
                    <Send size={14} /> 发送
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB: Reports ═══════ */}
      {activeTab === 'reports' && (
        <div className="flex" style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Run List */}
          <div className="card" style={{ flex: '0 0 320px', minWidth: 260, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <h4 style={{ marginTop: 0 }}><Clock size={16} style={{ marginRight: 6 }} />历史报告</h4>
            {loadingRuns ? (
              <div className="text-center muted py-4"><Loader2 size={18} className="spin" /> 加载中...</div>
            ) : testRuns.length === 0 ? (
              <div className="text-center muted py-4">暂无测试报告</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {testRuns.map(run => (
                  <div
                    key={run.id}
                    className={`report-item ${selectedRunId === run.id ? 'active' : ''}`}
                    onClick={() => loadReport(run.id)}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      cursor: 'pointer',
                      border: '1px solid var(--border)',
                      background: selectedRunId === run.id ? 'var(--primary-light)' : 'var(--surface)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <strong style={{ fontSize: 13 }}>{run.name || '未命名测试'}</strong>
                      {statusBadge(run.status)}
                    </div>
                    <div className="flex items-center gap-2 muted" style={{ fontSize: 12, marginTop: 4 }}>
                      <span>{run.total_count} 条</span>
                      <span style={{ color: 'var(--success)' }}>{run.passed_count} 通过</span>
                      {run.failed_count > 0 && <span style={{ color: 'var(--danger)' }}>{run.failed_count} 失败</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {new Date(run.created_at).toLocaleString()}
                      {run.duration_ms && ` · ${run.duration_ms}ms`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Report Detail */}
          <div className="card" style={{ flex: 1, minWidth: 400, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            {!selectedRunId ? (
              <div className="text-center muted py-8">选择左侧报告查看详情</div>
            ) : loadingReport ? (
              <div className="text-center muted py-8"><Loader2 size={20} className="spin" /> 加载报告...</div>
            ) : !runReport ? (
              <div className="text-center muted py-8">报告加载失败</div>
            ) : (
              <>
                {/* Summary */}
                <div className="flex items-center gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0 }}>{runReport.name || '测试报告'}</h3>
                  {statusBadge(runReport.status)}
                </div>
                <div className="grid grid-cols-4 mb-4" style={{ gap: 12 }}>
                  <div className="stat-box">
                    <div className="stat-label">总数</div>
                    <div className="stat-value">{runReport.total_count}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">通过</div>
                    <div className="stat-value" style={{ color: 'var(--success)' }}>{runReport.passed_count}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">失败</div>
                    <div className="stat-value" style={{ color: 'var(--danger)' }}>{runReport.failed_count}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">通过率</div>
                    <div className="stat-value" style={{ color: passRateColor(runReport.pass_rate) }}>
                      {runReport.pass_rate !== null ? `${(runReport.pass_rate * 100).toFixed(1)}%` : '-'}
                    </div>
                  </div>
                </div>

                {/* Results */}
                <h4 style={{ marginTop: 16 }}>详细结果</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {runReport.results.map(r => (
                    <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                      <div
                        className="flex items-center justify-between"
                        style={{
                          padding: '10px 14px',
                          cursor: 'pointer',
                          background: r.status === 'passed' ? 'rgba(34,197,94,0.08)' : r.status === 'failed' ? 'rgba(239,68,68,0.08)' : 'var(--surface)',
                        }}
                        onClick={() => toggleResultExpand(r.id)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedResults.has(r.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <strong>#{r.sequence}</strong>
                          <span>{r.test_case_name || r.test_case_id.slice(0, 8)}</span>
                          {statusBadge(r.status)}
                        </div>
                        <div className="flex items-center gap-3 muted" style={{ fontSize: 12 }}>
                          {r.similarity_score !== null && <span>相似度: {(r.similarity_score * 100).toFixed(0)}%</span>}
                          {r.duration_ms && <span>{r.duration_ms}ms</span>}
                        </div>
                      </div>
                      {expandedResults.has(r.id) && (
                        <div style={{ padding: '12px 14px', background: 'var(--bg)', fontSize: 13 }}>
                          <div className="mb-2">
                            <div className="muted mb-1">输入:</div>
                            <div style={{ padding: 8, background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>{r.input_text}</div>
                          </div>
                          {r.actual_output && (
                            <div className="mb-2">
                              <div className="muted mb-1">实际输出:</div>
                              <div style={{ padding: 8, background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', whiteSpace: 'pre-wrap' }}>{r.actual_output}</div>
                            </div>
                          )}
                          {r.expected_output && (
                            <div className="mb-2">
                              <div className="muted mb-1">预期输出:</div>
                              <div style={{ padding: 8, background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>{r.expected_output}</div>
                            </div>
                          )}
                          {r.expected_contains && r.expected_contains.length > 0 && (
                            <div className="mb-2">
                              <div className="muted mb-1">预期关键词:</div>
                              <div className="flex gap-1 flex-wrap">
                                {r.expected_contains.map((kw, i) => {
                                  const matched = r.matched_keywords?.includes(kw);
                                  return (
                                    <span key={i} className={`badge ${matched ? 'badge-success' : 'badge-danger'}`}>
                                      {matched ? <CheckCircle2 size={12} style={{ marginRight: 4 }} /> : <XCircle size={12} style={{ marginRight: 4 }} />} {kw}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {r.matched_keywords && r.matched_keywords.length > 0 && (
                            <div className="mb-2">
                              <div className="muted mb-1">匹配到的关键词:</div>
                              <div className="flex gap-1 flex-wrap">
                                {r.matched_keywords.map((kw, i) => (
                                  <span key={i} className="badge badge-success">{kw}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {r.missing_keywords && r.missing_keywords.length > 0 && (
                            <div className="mb-2">
                              <div className="muted mb-1">缺失的关键词:</div>
                              <div className="flex gap-1 flex-wrap">
                                {r.missing_keywords.map((kw, i) => (
                                  <span key={i} className="badge badge-danger">{kw}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {r.error_message && (
                            <div style={{ color: 'var(--danger)', padding: 8, background: 'rgba(239,68,68,0.06)', borderRadius: 6 }}>
                              <AlertCircle size={14} style={{ marginRight: 4 }} />
                              {r.error_message}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {dialog}
    </div>
  );
}
